// src/components/admin/scheduling/components/ScheduleMonthView.js
//
// Admin Schedule Month View — Events-only continuous-scroll monthly calendar.
// - Vertically scrollable across months (infinite scroll, same pattern as Student Calendar)
// - Events displayed in Google-Calendar-style dot + time + title rows (no heavy cards)
// - Max-width configurable via --smv-max-width CSS variable in ScheduleMonthView.css
// - Clicking a day → navigates to Day view (drillToDate)
// - Clicking an event → opens Edit Event modal
// - Double-clicking empty space → opens Create Event modal

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  addMonths,
  monthGridDays,
  isSameMonth,
  isDisabledDay,
  startOfMonth,
} from "../../employee/availability/availabilityUtils";
import { computeEventsForDate } from "../eventUtils";
import { fetchDayConfigsForDates } from "../schedulingApi";
import "./ScheduleMonthView.css";

// ─── Constants ────────────────────────────────────────────────────────────────
const PAST_INIT   = 2;   // months to pre-load before current
const FUTURE_INIT = 3;   // months to pre-load after current
const LOAD_MORE   = 2;   // how many months to load per sentinel trigger

// ─── Helpers ──────────────────────────────────────────────────────────────────
function ymd(d) {
  const y  = d.getFullYear();
  const m  = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
}

function monthKey(d) {
  return `${d.getFullYear()}-${d.getMonth()}`;
}

function isTodayDate(date) {
  const t = new Date();
  return (
    date.getFullYear() === t.getFullYear() &&
    date.getMonth()    === t.getMonth()    &&
    date.getDate()     === t.getDate()
  );
}

function captureAnchor() {
  // Find the first [data-month-key] element that is visible in the viewport
  const nodes = document.querySelectorAll("[data-month-key]");
  for (const node of nodes) {
    const r = node.getBoundingClientRect();
    if (r.bottom > 0) {
      return { key: node.getAttribute("data-month-key"), top: r.top };
    }
  }
  return null;
}

// ─── Compact time formatter: "1pm", "1:45pm" — shorter than "1:00 PM" ─────────
function formatCompactTime(minutes) {
  const h24 = Math.floor(minutes / 60) % 24;
  const m   = minutes % 60;
  const ampm = h24 >= 12 ? "pm" : "am";
  const h12  = h24 % 12 || 12;
  if (m === 0) return `${h12}${ampm}`;
  return `${h12}:${String(m).padStart(2, "0")}${ampm}`;
}

// ─── Event row — Google-Calendar dot style ────────────────────────────────────
function EventRow({ event, onEventClick }) {
  const timeLabel = event.startTime != null
    ? formatCompactTime(event.startTime)
    : null;
  const failed    = event.syncStatus === "sync_failed";

  return (
    <div
      className={`smv-eventRow${failed ? " smv-eventRow--failed" : ""}`}
      onClick={(e) => { e.stopPropagation(); onEventClick?.(event); }}
      title={event.title + (event.notes ? `\n${event.notes}` : "")}
    >
      <span className={`smv-dot${failed ? " smv-dot--failed" : ""}`} aria-hidden="true" />
      {timeLabel && <span className="smv-eventTime">{timeLabel}</span>}
      <span className="smv-eventTitle">{event.title}</span>
      {event.recurrence?.enabled && (
        <span className="smv-eventRecur" title="Recurring">↺</span>
      )}
    </div>
  );
}

// ─── Day popover (shown when clicking "N more") ───────────────────────────────
function DayPopover({ date, events, onClose, onEventClick, onCreateEvent }) {
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const dayName = date.toLocaleDateString("en-US", { weekday: "short" });

  return (
    <div className="smv-popover" ref={ref} onClick={(e) => e.stopPropagation()}>
      <div className="smv-popoverHeader">
        <div className="smv-popoverDate">
          <span className="smv-popoverDayName">{dayName}</span>
          <span className={`smv-popoverDayNum${isTodayDate(date) ? " smv-popoverDayNum--today" : ""}`}>
            {date.getDate()}
          </span>
        </div>
        <button className="smv-popoverClose" onClick={onClose} aria-label="Close">✕</button>
      </div>
      <div className="smv-popoverEvents">
        {events.length === 0 ? (
          <div className="smv-popoverEmpty">No events</div>
        ) : (
          events.map((evt) => (
            <EventRow
              key={evt.id}
              event={evt}
              onEventClick={(e) => { onEventClick(e); onClose(); }}
            />
          ))
        )}
      </div>
      {onCreateEvent && (
        <button
          className="smv-popoverCreate"
          onClick={() => { onCreateEvent(date); onClose(); }}
        >
          + Add event
        </button>
      )}
    </div>
  );
}

// ─── Single day cell ─────────────────────────────────────────────────────────
const MAX_VISIBLE_EVENTS = 3;

function EventDayCell({
  date, month, dayConfigsMap,
  allEvents, popoverDateKey, setPopoverDateKey,
  onDayClick, onEventClick, onCreateEvent,
  selectedDate,
}) {
  const inMonth    = isSameMonth(date, month);
  const key        = ymd(date);
  const isClosed   = isDisabledDay(date, dayConfigsMap);
  const isToday    = isTodayDate(date);
  const isSelected = !!selectedDate && key === ymd(selectedDate);
  const isPopover  = popoverDateKey === key;

  const cellEvents = useMemo(
    () => (inMonth ? computeEventsForDate(allEvents, key) : []),
    [allEvents, key, inMonth]
  );
  const visible     = cellEvents.slice(0, MAX_VISIBLE_EVENTS);
  const hiddenCount = Math.max(0, cellEvents.length - MAX_VISIBLE_EVENTS);

  // Off-month cells: completely blank — no date number, no events, no styling.
  // The cell still exists structurally to keep the 7-column grid alignment correct.
  if (!inMonth) {
    return <div className="smv-cell smv-cell--other" />;
  }

  return (
    <div
      className={[
        "smv-cell",
        isClosed  ? "smv-cell--closed"  : "",
        isToday   ? "smv-cell--today"   : "",
        isSelected ? "smv-cell--selected" : "",
        isPopover ? "smv-cell--popover" : "",
      ].filter(Boolean).join(" ")}
      data-date-key={key}
      onClick={() => { if (!isClosed) { onDayClick?.(date); setPopoverDateKey(null); } }}
      onDoubleClick={(e) => { e.stopPropagation(); if (!isClosed) onCreateEvent?.(date); }}
    >
      {/* Date number (top-right like StudentCalendar) */}
      <div className="smv-cellDateRow">
        <span className={isToday ? "smv-todayCircle" : "smv-cellDateNum"}>
          {date.getDate()}
        </span>
      </div>

      {/* Events */}
      <div className="smv-cellEvents">
        {visible.map((evt) => (
          <EventRow key={evt.id} event={evt} onEventClick={onEventClick} />
        ))}
        {hiddenCount > 0 && (
          <button
            className="smv-moreBtn"
            onClick={(e) => { e.stopPropagation(); setPopoverDateKey(isPopover ? null : key); }}
          >
            {hiddenCount} more
          </button>
        )}
      </div>

      {/* Popover */}
      {isPopover && (
        <DayPopover
          date={date}
          events={cellEvents}
          onClose={() => setPopoverDateKey(null)}
          onEventClick={onEventClick}
          onCreateEvent={onCreateEvent}
        />
      )}
    </div>
  );
}

// ─── Month section ────────────────────────────────────────────────────────────
function MonthBlock({ month, monthRef, isCurrentMonth, dayConfigsMap, allEvents, popoverDateKey, setPopoverDateKey, onDayClick, onEventClick, onCreateEvent, selectedDate }) {
  const days  = useMemo(() => monthGridDays(month), [month]);
  const weeks = useMemo(() => {
    const rows = [];
    for (let i = 0; i < days.length; i += 7) {
      const week = days.slice(i, i + 7);
      if (week.some((d) => isSameMonth(d, month))) rows.push(week);
    }
    return rows;
  }, [days, month]);

  return (
    <div data-month-key={monthKey(month)} ref={monthRef} style={{ scrollMarginTop: 40 }}>
      {/* Month label */}
      <div className="smv-monthLabel">
        <span className="smv-monthName">
          {month.toLocaleDateString("en-US", { month: "long" })}
        </span>
        <span className="smv-monthYear">{month.getFullYear()}</span>
        {isCurrentMonth && <span className="smv-thisMonthBadge">This Month</span>}
      </div>

      {/* Day-of-week header */}
      <div className="smv-dowHeader">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d, i) => (
          <div
            key={d}
            className={`smv-dowCell${i === 0 || i === 6 ? " smv-dowCell--weekend" : ""}`}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Week rows */}
      <div className="smv-grid">
        {weeks.map((week, wi) => (
          <div key={wi} className="smv-weekRow">
            {week.map((date, idx) => (
              <EventDayCell
                key={`${ymd(date)}-${idx}`}
                date={date}
                month={month}
                dayConfigsMap={dayConfigsMap}
                allEvents={allEvents}
                popoverDateKey={popoverDateKey}
                setPopoverDateKey={setPopoverDateKey}
                onDayClick={onDayClick}
                onEventClick={onEventClick}
                onCreateEvent={onCreateEvent}
                selectedDate={selectedDate}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function ScheduleMonthView({
  monthCursor,          // Date — focus month (from WeekBar prev/next)
  selectedDate,        // Date — active clicked date in month view
  scrollTrigger = 0,    // Increment this from parent to force scroll to today's month
  allEvents = [],       // Events from AdminScheduling subscription
  dayConfigsMap: propDayConfigs = {},  // Seed configs from parent
  onEventClick,         // (event) → opens Edit Event modal
  onCreateEvent,        // (date) → opens Create Event modal
  onDrillToDate,        // (date) → navigate to Day view
}) {
  const BASE  = useMemo(() => startOfMonth(new Date()), []);
  const today = useMemo(() => new Date(), []);

  // ── Multi-month state ──
  const [months, setMonths] = useState(() =>
    Array.from({ length: PAST_INIT + 1 + FUTURE_INIT }, (_, i) =>
      addMonths(BASE, i - PAST_INIT)
    )
  );
  const [pastCount, setPastCount] = useState(PAST_INIT);

  // ── Day configs: seed from prop, fetch more as months load ──
  const [dayConfigsMap, setDayConfigsMap] = useState(propDayConfigs);

  // Merge prop configs whenever they change (parent might refresh them)
  useEffect(() => {
    setDayConfigsMap((prev) => ({ ...prev, ...propDayConfigs }));
  }, [propDayConfigs]);

  // ── Refs ──
  const currentMonthRef   = useRef(null);
  const isLoadingPastRef  = useRef(false);
  const isLoadingFutRef   = useRef(false);
  const loadedStartRef    = useRef(-PAST_INIT);
  const loadedEndRef      = useRef(FUTURE_INIT);
  const topSentinelRef    = useRef(null);
  const bottomSentinelRef = useRef(null);
  const [popoverDateKey, setPopoverDateKey] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // ── Scroll to current month on mount ──
  useEffect(() => {
    setTimeout(() => {
      currentMonthRef.current?.scrollIntoView({ block: "start", behavior: "instant" });
    }, 60);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load day configs for initial months ──
  useEffect(() => {
    const allDays = months.flatMap((m) => monthGridDays(m));
    fetchDayConfigsForDates(allDays)
      .then((cfgs) => setDayConfigsMap((prev) => ({ ...prev, ...cfgs })))
      .catch(console.error);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── When monthCursor changes (WeekBar prev/next arrows or date selection),
  // ensure the month grid includes the target month and scrolls to it.
  const isFirstCursorRender = useRef(true);
  useEffect(() => {
    if (!monthCursor) return;
    const key = monthKey(monthCursor);
    const existing = months.some((m) => monthKey(m) === key);

    if (isFirstCursorRender.current) {
      isFirstCursorRender.current = false;
      const el = document.querySelector(`[data-month-key="${key}"]`);
      if (el) el.scrollIntoView({ block: "start", behavior: "instant" });
      return;
    }

    if (existing) {
      const el = document.querySelector(`[data-month-key="${key}"]`);
      if (el) el.scrollIntoView({ block: "start", behavior: "smooth" });
      return;
    }

    const newStart = addMonths(monthCursor, -PAST_INIT);
    const rebuilt = Array.from({ length: PAST_INIT + 1 + FUTURE_INIT }, (_, i) => addMonths(newStart, i));
    setMonths(rebuilt);
    setPastCount(PAST_INIT);
    loadedStartRef.current = -PAST_INIT;
    loadedEndRef.current = FUTURE_INIT;

    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-month-key="${key}"]`);
      if (el) el.scrollIntoView({ block: "start", behavior: "smooth" });
    });
  }, [monthCursor, months]);

  // ── When Today is clicked in Month view, or the selected date changes to a different
  // month, scroll the main month calendar to the active monthCursor instead of always
  // snapping back to today's month.
  useEffect(() => {
    if (!scrollTrigger || !monthCursor) return; // 0 on initial render → skip
    const key = monthKey(monthCursor);
    const el = document.querySelector(`[data-month-key="${key}"]`);
    if (el) el.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [scrollTrigger, monthCursor]);

  useEffect(() => {
    if (!selectedDate) return;
    const selectedKey = ymd(selectedDate);
    const el = document.querySelector(`[data-date-key="${selectedKey}"]`);
    if (el) {
      el.scrollIntoView({ block: "center", behavior: "smooth", inline: "nearest" });
    }
  }, [selectedDate]);

  // ── Load more future months ──
  const loadMoreFuture = useCallback(async () => {
    if (isLoadingFutRef.current) return;
    isLoadingFutRef.current = true;
    setIsLoadingMore(true);
    const prevEnd = loadedEndRef.current;
    const newEnd  = prevEnd + LOAD_MORE;
    const newMons = Array.from({ length: LOAD_MORE }, (_, i) => addMonths(BASE, prevEnd + 1 + i));
    try {
      const allDays = newMons.flatMap((m) => monthGridDays(m));
      const cfgs    = await fetchDayConfigsForDates(allDays);
      setDayConfigsMap((prev) => ({ ...prev, ...cfgs }));
      setMonths((prev) => [...prev, ...newMons]);
      loadedEndRef.current = newEnd;
    } catch (e) {
      console.error(e);
    } finally {
      isLoadingFutRef.current = false;
      setIsLoadingMore(false);
    }
  }, [BASE]);

  // ── Load more past months ──
  const loadMorePast = useCallback(async () => {
    if (isLoadingPastRef.current) return;
    isLoadingPastRef.current = true;
    const prevStart = loadedStartRef.current;
    const newStart  = prevStart - LOAD_MORE;
    const newMons   = Array.from({ length: LOAD_MORE }, (_, i) => addMonths(BASE, newStart + i));
    try {
      const allDays = newMons.flatMap((m) => monthGridDays(m));
      const cfgs    = await fetchDayConfigsForDates(allDays);

      // Capture the anchor before DOM update
      const anchor = captureAnchor();

      setDayConfigsMap((prev) => ({ ...prev, ...cfgs }));
      setMonths((prev) => [...newMons, ...prev]);
      setPastCount((p) => p + LOAD_MORE);
      loadedStartRef.current = newStart;

      // After DOM update, restore scroll position so viewport doesn't jump
      if (anchor?.key) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const anchorNode = document.querySelector(`[data-month-key="${anchor.key}"]`);
            if (!anchorNode) return;
            const newTop = anchorNode.getBoundingClientRect().top;
            const delta  = newTop - anchor.top;
            if (Math.abs(delta) > 2) window.scrollBy({ top: delta, behavior: "instant" });
          });
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      isLoadingPastRef.current = false;
    }
  }, [BASE]);

  // ── IntersectionObserver for infinite scroll ──
  // No explicit root — uses the viewport (parent adminSchedWrap handles page scrolling).
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          if (e.target === topSentinelRef.current)    loadMorePast();
          if (e.target === bottomSentinelRef.current) loadMoreFuture();
        }
      },
      { rootMargin: "800px" } // trigger well before sentinel reaches viewport
    );
    if (topSentinelRef.current)    obs.observe(topSentinelRef.current);
    if (bottomSentinelRef.current) obs.observe(bottomSentinelRef.current);
    return () => obs.disconnect();
  }, [loadMorePast, loadMoreFuture]);

  return (
    <div className="smv-outer">
      <div className="smv-inner">
        {/* Top sentinel */}
        <div ref={topSentinelRef} style={{ height: 1 }} />

        {months.map((month, mi) => {
          const isCurrentMonth =
            month.getFullYear() === today.getFullYear() &&
            month.getMonth()    === today.getMonth();

          return (
            <MonthBlock
              key={monthKey(month)}
              month={month}
              monthRef={mi === pastCount ? currentMonthRef : null}
              isCurrentMonth={isCurrentMonth}
              dayConfigsMap={dayConfigsMap}
              allEvents={allEvents}
              popoverDateKey={popoverDateKey}
              setPopoverDateKey={setPopoverDateKey}
              onDayClick={onDrillToDate}
              onEventClick={onEventClick}
              onCreateEvent={onCreateEvent}
              selectedDate={selectedDate}
            />
          );
        })}

        {isLoadingMore && (
          <div className="smv-loading">Loading…</div>
        )}

        {/* Bottom sentinel */}
        <div ref={bottomSentinelRef} style={{ height: 1 }} />
      </div>
    </div>
  );
}
